/**
 * 单图上传组件（SingleImageUpload）
 */

import type { ReactElement, ChangeEvent, DragEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Upload, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { createUploadSessionId, uploadService } from '@/services/uploadService'
import type { FormFieldProps } from './types'

/**
 * 单图上传组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function SingleImageUploadField({
  component,
  value,
  onChange,
  error,
  disabled = false,
}: FormFieldProps): ReactElement {
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const uploadSessionIdRef = useRef(createUploadSessionId())
  const previousImageUrlRef = useRef('')

  const imageUrl = typeof value === 'string' ? value : ''

  useEffect(() => {
    if (previousImageUrlRef.current !== '' && imageUrl === '') {
      uploadSessionIdRef.current = createUploadSessionId()
    }
    previousImageUrlRef.current = imageUrl
  }, [imageUrl])

  /**
   * 上传文件
   * @param file 文件对象
   */
  async function uploadFile(file: File): Promise<void> {
    console.log('[SingleImageUpload] 开始上传文件:', file.name, file.type, file.size)

    if (!file.type.startsWith('image/')) {
      console.warn('[SingleImageUpload] 文件类型不支持:', file.type)
      setUploadError('请上传图片文件')
      return
    }

    // 检查文件大小（8MB）
    if (file.size > 8 * 1024 * 1024) {
      console.warn('[SingleImageUpload] 文件过大:', file.size)
      setUploadError('图片大小不能超过 8MB')
      return
    }

    setIsUploading(true)
    setUploadError(null)

    try {
      console.log('[SingleImageUpload] 调用 uploadService.uploadFile...')
      const url = await uploadService.uploadFile(file, undefined, uploadSessionIdRef.current)
      console.log('[SingleImageUpload] 上传成功:', url)
      onChange(url)
    } catch (err) {
      console.error('[SingleImageUpload] 上传失败:', err)
      const message = err instanceof Error ? err.message : '上传失败'
      setUploadError(message)
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * 处理文件选择
   * @param event 选择事件
   */
  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0]
    if (file !== undefined) {
      void uploadFile(file)
    }
    // 清空 input，允许重复选择同一文件
    event.target.value = ''
  }

  /**
   * 处理拖拽进入
   * @param event 拖拽事件
   */
  function handleDragOver(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    event.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragOver(true)
    }
  }

  /**
   * 处理拖拽离开
   * @param event 拖拽事件
   */
  function handleDragLeave(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
  }

  /**
   * 处理拖拽放下
   * @param event 拖拽事件
   */
  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)

    if (disabled || isUploading) {
      return
    }

    const file = event.dataTransfer.files[0]
    if (file !== undefined) {
      void uploadFile(file)
    }
  }

  /**
   * 点击选择文件
   */
  function handleClick(): void {
    if (!disabled && !isUploading) {
      inputRef.current?.click()
    }
  }

  /**
   * 移除图片
   */
  function handleRemove(): void {
    onChange('')
    setUploadError(null)
  }

  const displayError = error ?? uploadError

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">
        {component.title}
        {component.is_required && (
          <span className="ml-1 text-destructive">*</span>
        )}
      </label>

      {imageUrl !== '' ? (
        // 已上传图片预览
        <div className="relative inline-block">
          <img
            src={imageUrl}
            alt="已上传图片"
            className="h-32 w-32 rounded-lg border object-cover"
          />
          {!disabled && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -right-2 -top-2 h-6 w-6 rounded-full"
              onClick={handleRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        // 上传区域
        <div
          role="button"
          tabIndex={disabled || isUploading ? -1 : 0}
          onClick={handleClick}
          onKeyDown={(e): void => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClick()
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-all',
            'hover:border-primary hover:bg-primary/5',
            isDragOver && 'border-primary bg-primary/10',
            displayError !== null && 'border-destructive',
            (disabled || isUploading) && 'cursor-not-allowed opacity-50'
          )}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">上传中...</span>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                点击或拖拽上传图片
              </span>
              <span className="text-xs text-muted-foreground/60">
                支持 JPG、PNG、WebP，最大 8MB
              </span>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {displayError !== null && (
        <p className="text-xs text-destructive">{displayError}</p>
      )}
    </div>
  )
}
